import {
  Form,
  FormAssertive,
  FormHeader,
  FormImage,
  FormInput,
  FormSection,
  FormSubmit,
  useForm,
} from "@/components/form"
import { Button } from "@/components/ui/button"
import { Dialog } from "@/components/ui/dialog"
import { resetAllStoresAndReload } from "@/store"
import { updateSession } from "@/store/auth/actions"
import { useMe } from "@/store/auth/hooks"
import { updateProfile } from "@/store/users/actions"
import { User } from "@/store/users/localizers"
import {} from "lucide-react"
import { match } from "ts-pattern"
import { UseDialogFormProps, UseDialogProps } from "../ui/hooks/useDialog"

/**
 * dictionary src/dictionaries/en/components/dialogs/user-profile.json
 */
const dictionary = createContextMapper("components", "dialogs", "user-profile")

/**
 * UserProfileDialog
 *
 */
export const UserProfileDialog: React.FC<UseDialogProps<User>> = ({ item, ...props }) => {
  const { _ } = useDictionary(dictionary())
  return (
    <Dialog {...props} title={_("title")} description={_("secondary")} className="sm:max-w-2xl">
      {item !== false && <DialogForm {...props} item={item} />}
    </Dialog>
  )
}

/**
 * DialogForm
 */
const DialogForm: React.FC<UseDialogFormProps<User>> = ({ item: user, onOpenChange }) => {
  const { _ } = useDictionary(dictionary())
  const _form = useFormDictionary()
  const _errors = useErrorsDictionary()
  const isMe = useMe().id === user.id
  const form = useForm({
    values: {
      ...D.selectKeys(user.profile, baseFields),
      image: {
        url: user.profile.image,
        file: null,
        delete: false,
      },
    },
    onSubmit: async ({ values }) => {
      const payload = {
        ...D.selectKeys(values, baseFields),
        image: D.selectKeys(values.image, ["file", "delete"]),
      }
      return match(await updateProfile(user.id, payload))
        .with({ error: false }, () => {
          onOpenChange(false)
          toast.success(_("success"))
          if (isMe) updateSession()
        })
        .otherwise(({ code }) =>
          match(code)
            .with("VALIDATION_FAILURE", _errors)
            .with("INVALID_AUTH_SESSION", resetAllStoresAndReload)
            .otherwise(() => void toast.error(_errors("FETCH_ERROR")))
        )
    },
  })

  return (
    <Form form={form} className="grid gap-6">
      <FormAssertive />
      <FormHeader>
        <FormHeader.Title>{_("section-personal-title")}</FormHeader.Title>
        <FormHeader.Description>{_("section-personal-description")}</FormHeader.Description>
      </FormHeader>
      <FormSection className="@xl/dialog:grid grid-cols-3">
        <FormSection className="col-span-2">
          <FormInput
            label={_form("firstname-label")}
            name="firstname"
            placeholder={_form("firstname-placeholder")}
            auto="given-name"
          />
          <FormInput
            label={_form("lastname-label")}
            name="lastname"
            placeholder={_form("lastname-placeholder")}
            auto="family-name"
          />
          <FormInput
            label={_form("email-label")}
            name="email"
            placeholder={_form("email-placeholder")}
            auto="email"
          />
        </FormSection>
        <div>
          <FormImage label={_("image-label")} name="image" ratio="aspect-square" />
        </div>
      </FormSection>

      <FormHeader>
        <FormHeader.Title>{_("section-employment-title")}</FormHeader.Title>
        <FormHeader.Description>{_("section-employment-description")}</FormHeader.Description>
      </FormHeader>
      <FormSection className="@xl/dialog:grid grid-cols-2">
        <FormInput
          label={_form("company-label")}
          name="company"
          placeholder={_form("company-placeholder")}
          auto="organization"
        />
        <FormInput
          label={_form("position-label")}
          name="position"
          placeholder={_form("position-placeholder")}
          auto="organization-title"
        />
      </FormSection>

      <Dialog.Footer className="sm:justify-start">
        <Dialog.Close asChild>
          <Button variant="secondary">{_form("cancel")}</Button>
        </Dialog.Close>
        <FormSubmit>{_form("update")}</FormSubmit>
      </Dialog.Footer>
    </Form>
  )
}
const baseFields = ["firstname", "lastname", "position", "company", "email"] as const
